import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { formatPrice, getArtworkTitle } from "@/lib/utils";
import type { Artwork } from "@/lib/api";

interface ArtworkCardProps {
  artwork: Artwork;
}

export default function ArtworkCard({ artwork }: ArtworkCardProps) {
  const { locale, t } = useI18n();
  const title = getArtworkTitle(artwork, locale);
  const isSold = artwork.status === "sold";

  return (
    <Link to={`/artworks/${artwork.slug}`} className="artwork-card group">
      <div className="relative overflow-hidden">
        <img
          src={artwork.image_url || "/placeholder-artwork.jpg"}
          alt={title}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span
            className="rounded-full px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em]"
            style={{
              background: "rgba(255, 251, 245, 0.92)",
              color: "var(--color-charcoal)",
              backdropFilter: "blur(12px)",
            }}
          >
            {artwork.technique || artwork.category}
          </span>
          {isSold && (
            <span
              className="rounded-full px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em]"
              style={{ background: "var(--color-charcoal)", color: "white" }}
            >
              {t.artwork.sold}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3 p-5">
        <div className="card-meta">
          <h3 className="text-[1.45rem] group-hover:text-[var(--color-terracotta)] transition-colors">
            {title}
          </h3>
          <ArrowUpRight size={18} style={{ color: "var(--color-text-light)" }} />
        </div>

        <div className="card-meta text-sm">
          <span style={{ color: "var(--color-text-light)" }}>{artwork.dimensions || artwork.category}</span>
          <strong
            className="text-sm"
            style={{ color: isSold ? "var(--color-text-light)" : "var(--color-terracotta)" }}
          >
            {isSold ? "Unavailable" : formatPrice(artwork.price, artwork.currency)}
          </strong>
        </div>
      </div>
    </Link>
  );
}
